import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Avatar
} from '@mui/material';
import { Security, Person } from '@mui/icons-material';
import { authService } from '../../services/auth';

const ManagerPasswordDialog = ({ open, onClose, user, onSuccess }) => {
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setPasswordData({ newPassword: '', confirmPassword: '' });
      setError('');
    }
  }, [open]);

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handlePasswordUpdate = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await authService.changePassword({
        user_id: user.id,
        new_password: passwordData.newPassword,
      });

      onSuccess && onSuccess(`Password changed successfully for ${user.first_name} ${user.last_name}`);
      onClose();
    } catch (error) {
      console.error('Failed to change password:', error);
      setError(
        error.response?.data?.message || 
        error.response?.data?.detail || 
        'Failed to change password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const isValid = passwordData.newPassword && 
                  passwordData.confirmPassword && 
                  passwordData.newPassword === passwordData.confirmPassword &&
                  passwordData.newPassword.length >= 8;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Security color="primary" />
          Change User Password
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {user && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                {user.first_name?.[0] || user.username?.[0] || 'U'}
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  {user.first_name} {user.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  @{user.username} • {user.role?.replace('_', ' ').toUpperCase()}
                </Typography>
                {user.email && (
                  <Typography variant="caption" color="text.secondary">
                    {user.email}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              name="newPassword"
              label="New Password"
              type="password"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              fullWidth
              required
              disabled={loading}
              helperText="Password must be at least 8 characters long"
              autoComplete="new-password"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              name="confirmPassword"
              label="Confirm New Password"
              type="password"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              fullWidth
              required
              disabled={loading}
              autoComplete="new-password"
              error={
                passwordData.newPassword !== passwordData.confirmPassword && 
                passwordData.confirmPassword !== ''
              }
              helperText={
                passwordData.newPassword !== passwordData.confirmPassword && 
                passwordData.confirmPassword !== ''
                  ? 'Passwords do not match'
                  : ''
              }
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.light' }}>
          <Typography variant="body2" color="warning.dark">
            <strong>Manager Override:</strong> As a manager, you can change this user's password without knowing their current password.
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handlePasswordUpdate}
          variant="contained"
          color="primary"
          disabled={loading || !isValid}
          startIcon={loading ? <CircularProgress size={16} /> : <Security />}
        >
          {loading ? 'Changing Password...' : 'Change Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManagerPasswordDialog;